import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  TrendingUp, Users, Package, DollarSign, LogOut, LayoutDashboard,
  LineChart, Settings, X, Shield, MessageCircle, Bell, Wallet,
  ShieldCheck, Building2, BarChart3, ChevronDown, ChevronRight,
} from "lucide-react";
import { useState } from "react";

const menuGroups = [
  {
    label: "Overview",
    items: [
      { id: "dashboard", icon: BarChart3, label: "Dashboard" },
    ],
  },
  {
    label: "Support",
    items: [
      { id: "support", icon: MessageCircle, label: "Support Tickets" },
    ],
  },
  {
    label: "Management",
    collapsible: true,
    items: [
      { id: "users", icon: Users, label: "Users" },
      { id: "packages", icon: Package, label: "Packages" },
      { id: "transactions", icon: DollarSign, label: "Transactions" },
      { id: "investments", icon: TrendingUp, label: "Investments" },
      { id: "kyc", icon: ShieldCheck, label: "KYC Verifications" },
    ],
  },
  {
    label: "Settings",
    collapsible: true,
    items: [
      { id: "crypto", icon: Wallet, label: "Crypto Addresses" },
      { id: "bank", icon: Building2, label: "Bank Account" },
      { id: "email", icon: Settings, label: "Email Provider" },
      { id: "telegram", icon: Bell, label: "Telegram" },
      { id: "notifications", icon: Bell, label: "Notifications" },
      { id: "templates", icon: Settings, label: "Email Templates" },
      { id: "push", icon: Bell, label: "Send Notification" },
    ],
  },
];

const navLinks = [
  { icon: LayoutDashboard, label: "Dashboard", href: "/dashboard" },
  { icon: LineChart, label: "Trading", href: "/trading" },
  { icon: Package, label: "Packages", href: "/packages" },
];

export const AdminSidebar = ({ activeSection, setActiveSection, user, onLogout, sidebarOpen, setSidebarOpen }) => {
  const [expandedGroups, setExpandedGroups] = useState({ Management: true, Settings: true });

  const toggleGroup = (label) => {
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <aside
      className={`fixed lg:relative inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform lg:translate-x-0 ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}
      data-testid="admin-sidebar"
    >
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-base font-semibold text-slate-800">Admin</span>
          </Link>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Quick Nav */}
        <div className="px-3 pt-3 pb-1">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold px-3 mb-1">Quick Nav</p>
          {navLinks.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 text-sm transition-colors"
            >
              <item.icon className="w-4 h-4" />
              <span>{item.label}</span>
            </Link>
          ))}
        </div>

        {/* Admin Sections */}
        <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
          {menuGroups.map((group) => (
            <div key={group.label}>
              {group.collapsible ? (
                <button
                  onClick={() => toggleGroup(group.label)}
                  className="flex items-center justify-between w-full px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold hover:text-slate-600"
                >
                  <span>{group.label}</span>
                  {expandedGroups[group.label] ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronRight className="w-3 h-3" />
                  )}
                </button>
              ) : (
                <p className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                  {group.label}
                </p>
              )}
              {(!group.collapsible || expandedGroups[group.label]) &&
                group.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveSection(item.id);
                      setSidebarOpen(false);
                    }}
                    className={`flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm transition-all ${
                      activeSection === item.id
                        ? "bg-amber-50 text-amber-700 font-medium"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-800"
                    }`}
                    data-testid={`sidebar-${item.id}`}
                  >
                    <item.icon className={`w-4 h-4 ${activeSection === item.id ? "text-amber-600" : ""}`} />
                    <span>{item.label}</span>
                  </button>
                ))}
            </div>
          ))}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-gradient-to-br from-rose-100 to-rose-200 rounded-full flex items-center justify-center">
              <span className="text-rose-700 font-semibold text-sm">A</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-slate-700 font-medium text-sm truncate">{user?.name}</p>
              <p className="text-rose-500 text-xs">Administrator</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full border-slate-300 text-slate-600 hover:bg-slate-100 gap-2 text-xs"
            onClick={onLogout}
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign Out
          </Button>
        </div>
      </div>
    </aside>
  );
};
